import { FC } from 'react'
import { PrescricaoType } from '_metronic/helpers'
import { ValoresItem, ItemPrescricao } from '../../core/montagem/itens'
import { CampoPendente, idDoCampo } from '../../core/montagem/pendencias'
import {
  UNIDADES_QUANTIDADE,
  completarComUnidade,
  deveSugerirUnidade,
} from '../../core/montagem/unidades'
import { termoUsoDoReceituario } from '../../core/receituario'
import styles from '../../Prescricao.module.scss'
import { Campo } from '../comum/Campo'
import { useRotulos } from '../comum/useRotulos'
import { InfoAlert, Radio } from 'app/components'

export type ErrosDoItem = Partial<Record<CampoPendente, string>>

type IProps = {
  item: ItemPrescricao
  atendeSUS: boolean
  erros: ErrosDoItem
  onChange: (campos: ValoresItem) => void
}

/** Campos editáveis do item, conforme a categoria. */
export const CamposDoItem: FC<IProps> = ({ item, atendeSUS, erros, onChange }) => {
  const { t } = useRotulos()
  const id = (campo: Parameters<typeof idDoCampo>[1]) => idDoCampo(item.chave, campo)

  if (item.categoria === PrescricaoType.Orientacoes) {
    // O texto não se edita no card: só pelo lápis (modal), e só na orientação do próprio médico.
    return <p className={`${styles.composicao} m-0`}>{item.texto}</p>
  }

  if (item.categoria === PrescricaoType.Exame) {
    return (
      <div className='d-flex flex-column gap-4'>
        <Campo
          id={id('instrucoes')}
          rotulo={t('prescricao.campo.instrucoes')}
          valor={item.instrucoes}
          onChange={(instrucoes) => onChange({ instrucoes })}
          linhas={2}
          placeholder={t('prescricao.campo.instrucoes_placeholder')}
        />
        <Campo
          id={`${item.chave}-observacoes`}
          rotulo={t('prescricao.campo.observacoes')}
          valor={item.observacoes}
          onChange={(observacoes) => onChange({ observacoes })}
          linhas={2}
        />
        {atendeSUS && item.catalogo.apac && (
          <InfoAlert variant='info' size='sm' className='mb-0'>
            {t('prescricao.item.apac_depois')}
          </InfoAlert>
        )}
      </div>
    )
  }

  // Medicamento e fórmula
  const temLme = item.categoria === PrescricaoType.Medicamento && atendeSUS && !!item.catalogo.lme
  const termoUso =
    item.categoria === PrescricaoType.Medicamento
      ? termoUsoDoReceituario(item.catalogo.receituario)
      : undefined

  return (
    <div className='row g-5'>
      <div className='col-lg-7 d-flex flex-column gap-4'>
        {item.categoria === PrescricaoType.Formula && item.catalogo.composicao && (
          <div className='d-flex flex-column gap-1'>
            <span className='fs-7 fw-semibold'>{t('prescricao.campo.composicao')}</span>
            <p className={styles.composicao}>{item.catalogo.composicao}</p>
          </div>
        )}
        <Campo
          id={id('posologia')}
          rotulo={t('prescricao.campo.posologia')}
          valor={item.posologia}
          onChange={(posologia) => onChange({ posologia })}
          obrigatorio
          erro={erros.posologia}
          linhas={2}
          placeholder={t('prescricao.campo.posologia_placeholder')}
        />
        <Campo
          id={`${item.chave}-observacoes`}
          rotulo={t('prescricao.campo.observacoes')}
          valor={item.observacoes}
          onChange={(observacoes) => onChange({ observacoes })}
          linhas={2}
          placeholder={t('prescricao.campo.observacoes_placeholder')}
        />
      </div>
      <div className='col-lg-5 d-flex flex-column gap-4'>
        <Campo
          id={id('quantidade')}
          rotulo={t('prescricao.campo.quantidade')}
          valor={item.quantidade}
          onChange={(quantidade) => onChange({ quantidade })}
          obrigatorio
          erro={erros.quantidade}
          placeholder={t('prescricao.campo.quantidade_placeholder')}
          sugestoes={deveSugerirUnidade(item.quantidade) ? UNIDADES_QUANTIDADE : []}
          onSugestao={(unidade) =>
            onChange({ quantidade: completarComUnidade(item.quantidade, unidade) })
          }
        />
        {item.categoria === PrescricaoType.Medicamento && (
          <fieldset className='m-0 p-0 border-0'>
            <legend className='fs-7 fw-semibold mb-1'>{t('prescricao.campo.uso_continuo')}</legend>
            <Radio
              id={`${item.chave}-uso`}
              value={item.usoContinuo}
              onChange={(usoContinuo) => onChange({ usoContinuo })}
              options={[
                { id: 'sim', value: true, descricao: t('common.yes') },
                { id: 'nao', value: false, descricao: t('common.no') },
              ]}
            />
          </fieldset>
        )}
      </div>
      {/* Termo que a Anvisa exige junto com a receita (retinoide, anorexígeno): só informação. */}
      {termoUso && (
        <div className='col-12'>
          <InfoAlert variant='info' size='sm' role='note' className='mb-0'>
            {t('medicamento.exige_termo', { termo: termoUso })}
          </InfoAlert>
        </div>
      )}
      {/* Largura toda, embaixo dos campos: mesmo lugar do aviso da APAC no exame. */}
      {temLme && (
        <div className='col-12'>
          <InfoAlert variant='info' size='sm' className='mb-0'>
            {t('prescricao.item.lme_depois')}
          </InfoAlert>
        </div>
      )}
    </div>
  )
}
